import { useCallback, useMemo } from 'react';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
    fetchVirtualAppointmentOverviewDetail,
    fetchVirtualAppointmentsOverviewPage,
    type VirtualAppointmentOverviewItem,
    type VirtualAppointmentOverviewQuery
} from '../services/virtualAppointmentsOverview';
import {
    VIRTUAL_APPOINTMENT_OVERVIEW_DETAIL_KEY,
    VIRTUAL_APPOINTMENTS_OVERVIEW_LIST_KEY,
    VIRTUAL_APPOINTMENTS_OVERVIEW_PAGE_KEY
} from './queryKeys';

const ONE_MINUTE = 60 * 1000;
const MAX_DETAIL_RETRIES = 2;
const CLIENT_ERROR_MIN = 400;
const SERVER_ERROR_MIN = 500;
const NO_ITEMS: VirtualAppointmentOverviewItem[] = [];
const noop = (): void => undefined;

export type OverviewTableMode = 'infinite' | 'paged';

// One shape for both modes, so the table never branches on how rows were fetched.
export interface OverviewListState {
    items: readonly VirtualAppointmentOverviewItem[];
    /** Undefined until the first response. */
    total: number | undefined;
    isLoading: boolean;
    isError: boolean;
    retry: () => void;
    hasMore: boolean;
    isLoadingMore: boolean;
    loadMore: () => void;
}

// A 4xx (deleted VA, stale or malformed link) will not heal on retry; network and 5xx might.
export function isVirtualAppointmentUnavailable(error: unknown): boolean {
    const e = error as { isAxiosError?: boolean; response?: { status?: number } } | null;
    const status = e?.isAxiosError === true ? e.response?.status : undefined;
    return status !== undefined && status >= CLIENT_ERROR_MIN && status < SERVER_ERROR_MIN;
}

function useInfiniteOverview(
    query: VirtualAppointmentOverviewQuery,
    enabled: boolean
): OverviewListState {
    const { data, isPending, isError, refetch, hasNextPage, fetchNextPage, isFetchingNextPage } =
        useInfiniteQuery({
            queryKey: VIRTUAL_APPOINTMENTS_OVERVIEW_LIST_KEY(query),
            queryFn: ({ pageParam }) =>
                fetchVirtualAppointmentsOverviewPage(query, { cursor: pageParam }),
            initialPageParam: null as string | null,
            getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
            staleTime: ONE_MINUTE,
            enabled
        });
    const items = useMemo(() => data?.pages.flatMap((page) => page.items) ?? NO_ITEMS, [data]);
    const retry = useCallback(() => void refetch(), [refetch]);
    const loadMore = useCallback(() => {
        if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
    return {
        items,
        total: data?.pages[0]?.total,
        isLoading: isPending,
        isError,
        retry,
        hasMore: hasNextPage,
        isLoadingMore: isFetchingNextPage,
        loadMore
    };
}

// The previous page stays on screen while the next loads, so paging never flashes a skeleton.
function usePagedOverview(
    query: VirtualAppointmentOverviewQuery,
    page: number,
    enabled: boolean
): OverviewListState {
    const { data, isPending, isError, refetch } = useQuery({
        queryKey: VIRTUAL_APPOINTMENTS_OVERVIEW_PAGE_KEY(query, page),
        queryFn: () => fetchVirtualAppointmentsOverviewPage(query, { page }),
        placeholderData: keepPreviousData,
        staleTime: ONE_MINUTE,
        enabled
    });
    const retry = useCallback(() => void refetch(), [refetch]);
    return {
        items: data?.items ?? NO_ITEMS,
        total: data?.total,
        isLoading: isPending,
        isError,
        retry,
        hasMore: false,
        isLoadingMore: false,
        loadMore: noop
    };
}

// Both queries are declared (hook order is fixed); only the active mode's one fetches.
export function useVirtualAppointmentsOverviewRows(
    query: VirtualAppointmentOverviewQuery,
    mode: OverviewTableMode,
    page: number
): OverviewListState {
    const infinite = useInfiniteOverview(query, mode === 'infinite');
    const paged = usePagedOverview(query, page, mode === 'paged');
    return mode === 'infinite' ? infinite : paged;
}

export function useVirtualAppointmentOverviewDetail(vaId: string | null) {
    return useQuery({
        queryKey: VIRTUAL_APPOINTMENT_OVERVIEW_DETAIL_KEY(vaId ?? ''),
        queryFn: () => fetchVirtualAppointmentOverviewDetail(vaId ?? ''),
        enabled: vaId !== null,
        staleTime: ONE_MINUTE,
        retry: (failureCount, error) =>
            !isVirtualAppointmentUnavailable(error) && failureCount < MAX_DETAIL_RETRIES
    });
}
